import { Injectable } from '@angular/core';

import { CloudRoom, Group } from '@shure/cloud/shared/models/devices';
import { ILogger } from '@shure/shared/angular/utils/logging';

function storeIntegrityError(message: string): Error {
	return new Error(`[GroupStoreService] index integrity violation: ${message}`);
}

function storeUsageError(message: string): Error {
	return new Error(`[GroupStoreService] usage error: ${message}`);
}

@Injectable({ providedIn: 'root' })
export class GroupStoreService {
	/**
	 *  All groups by nodeId
	 */
	protected groupIdToGroup = new Map<string, Group>();

	/**
	 *  All cloud rooms by nodeId
	 */
	protected cloudRoomIdToCloudRoom = new Map<string, CloudRoom>();

	/**
	 * Parent group nodeId to child group nodeIds
	 */
	protected groupIdToChildrenIds = new Map<string, Set<string>>();

	/**
	 * Group nodeId to member device nodeIds, including those in cloud rooms
	 */
	protected groupIdToMemberDeviceIds = new Map<string, Set<string>>();

	/**
	 *  CloudRoom nodeId to member device nodeIds
	 */
	protected roomIdToMemberDeviceIds = new Map<string, Set<string>>();

	/**
	 * Device nodeId to member group nodeId
	 */
	protected deviceIdToGroupId = new Map<string, string>();

	/**
	 *  Device nodeId to member CloudRoom nodeId
	 */
	protected deviceIdToRoomId = new Map<string, string>();

	/**
	 *  Group nodeId to ancestor group nodeId path (from root to current key)
	 */
	protected groupIdToIdPath = new Map<string, string[]>();

	constructor(private readonly logger: ILogger) {
		this.logger = logger.createScopedLogger('GroupStoreService');
	}

	//
	// Query methods
	//
	public get groupCount(): number {
		return this.groupIdToGroup.size;
	}

	public get cloudRoomCount(): number {
		return this.cloudRoomIdToCloudRoom.size;
	}

	/**
	 * @param groupNodeId
	 * @returns Name path from root ancestor to this group, e.g. ['Root', 'Parent', 'Current']
	 */
	public getGroupNamePath(groupNodeId: string): string[] {
		const path = this.groupIdToIdPath.get(groupNodeId);

		if (!path) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		return path.map((ancestorId) => {
			const group = this.groupIdToGroup.get(ancestorId);

			if (!group) {
				throw storeIntegrityError(
					`groupPathCacheMap references group "${ancestorId}" which does not exist in groupsMap`
				);
			}

			return group.name;
		});
	}

	/**
	 * @param groupNodeId
	 * @returns All descendant device nodeIds for the given groupNodeId, including those in cloud rooms
	 */
	public getDescendantDeviceIds(groupNodeId: string): string[] {
		const descendantDeviceIds: Set<string> = new Set<string>();

		const collectDevices = (currentGroupId: string): void => {
			const memberDeviceIds = this.groupIdToMemberDeviceIds.get(currentGroupId);

			if (memberDeviceIds) {
				for (const deviceId of memberDeviceIds) {
					descendantDeviceIds.add(deviceId);
				}
			}

			const childGroupIds = this.groupIdToChildrenIds.get(currentGroupId) ?? new Set<string>();

			for (const childGroupId of childGroupIds) {
				collectDevices(childGroupId);
			}
		};

		collectDevices(groupNodeId);

		return Array.from(descendantDeviceIds);
	}

	/**
	 * @param groupNodeId
	 * @returns Groups with the same parent as the given groupNodeId, excluding the group itself
	 */
	public getGroupSiblings(groupNodeId: string): Group[] {
		const group = this.getGroup(groupNodeId);

		// root level groups have no siblings
		if (!group.parentId) {
			return [];
		}

		return this.getGroupChildren(group.parentId).filter((sibling) => sibling.id !== groupNodeId);
	}

	/**
	 * @param groupNodeId - parent group nodeId
	 * @returns Child groups of the given parent
	 */
	public getGroupChildren(groupNodeId: string): Group[] {
		const childIds = Array.from(this.groupIdToChildrenIds.get(groupNodeId) ?? []);

		return childIds.map((childId) => {
			const child = this.groupIdToGroup.get(childId);

			if (!child) {
				throw storeIntegrityError(
					`childGroupsIndexMap references group "${childId}" which does not exist in groupsMap`
				);
			}

			return child;
		});
	}

	/**
	 * @param deviceNodeId
	 * @returns Member CloudRoom object and all member device nodeIds (inclusive), null if the device is not in a CloudRoom
	 */
	public getDeviceCloudRoomInfo(deviceNodeId: string): { cloudRoom: CloudRoom; deviceIds: string[] } | null {
		const cloudRoomId = this.deviceIdToRoomId.get(deviceNodeId);

		if (!cloudRoomId) {
			return null;
		}

		const cloudRoom = this.cloudRoomIdToCloudRoom.get(cloudRoomId);

		if (!cloudRoom) {
			throw storeIntegrityError(
				`deviceCloudRoomIndexMap references room "${cloudRoomId}" which does not exist in cloudRoomsMap`
			);
		}

		const deviceIds = Array.from(this.roomIdToMemberDeviceIds.get(cloudRoomId) ?? []);

		return { cloudRoom, deviceIds };
	}

	/**
	 * @param groupNodeId
	 * @returns Group object for the given groupNodeI
	 */
	public getGroup(groupNodeId: string): Group {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		return group;
	}

	/**
	 * @param cloudRoomNodeId
	 * @returns CloudRoom object for the given cloudRoomNodeId
	 */
	public getCloudRoom(cloudRoomNodeId: string): CloudRoom {
		const cloudRoom = this.cloudRoomIdToCloudRoom.get(cloudRoomNodeId);

		if (!cloudRoom) {
			throw storeUsageError(
				`cloudRoom "${cloudRoomNodeId}" was not found in the store. Ensure the passed cloudRoom nodeId is correct.`
			);
		}

		return cloudRoom;
	}

	/**
	 * @param deviceNodeId
	 * @returns Member group nodeId for the given deviceNodeId
	 */
	public getDeviceMemberGroupId(deviceNodeId: string): string {
		const groupNodeId = this.deviceIdToGroupId.get(deviceNodeId);

		if (!groupNodeId) {
			throw storeUsageError(
				`device "${deviceNodeId}" was not found in any group. Ensure the passed device nodeId is correct.`
			);
		}

		return groupNodeId;
	}

	//
	// Mutation methods
	//
	public addGroup(group: Group): void {
		this.groupIdToGroup.set(group.id, group);

		if (group.parentId) {
			const parentChildren = this.groupIdToChildrenIds.get(group.parentId) ?? new Set<string>();
			parentChildren.add(group.id);
			this.groupIdToChildrenIds.set(group.parentId, parentChildren);
		}

		this.buildPathCache(group.id);
	}

	public removeGroup(groupNodeId: string): void {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		if (this.groupIdToChildrenIds.get(groupNodeId)?.size) {
			throw storeUsageError(
				`group "${groupNodeId}" has child groups and cannot be removed. Remove child groups first.`
			);
		}

		if (this.groupIdToMemberDeviceIds.get(groupNodeId)?.size) {
			throw storeUsageError(
				`group "${groupNodeId}" has member devices and cannot be removed. Remove member devices first.`
			);
		}

		this.groupIdToMemberDeviceIds.delete(groupNodeId);

		// Remove cloud rooms belonging to this group
		for (const [roomId, room] of Array.from(this.cloudRoomIdToCloudRoom)) {
			if (room.groupId === groupNodeId) {
				this.roomIdToMemberDeviceIds.delete(roomId);
				this.cloudRoomIdToCloudRoom.delete(roomId);
			}
		}

		// Remove from parent's children index
		if (group.parentId) {
			this.groupIdToChildrenIds.get(group.parentId)?.delete(groupNodeId);
		}

		// Clean up own index entries
		this.groupIdToChildrenIds.delete(groupNodeId);
		this.groupIdToIdPath.delete(groupNodeId);
		this.groupIdToGroup.delete(groupNodeId);
	}

	public moveGroup(groupNodeId: string, newParentNodeId: string | undefined): void {
		const group = this.groupIdToGroup.get(groupNodeId);
		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		// Remove from old parent's children index
		if (group.parentId) {
			this.groupIdToChildrenIds.get(group.parentId)?.delete(groupNodeId);
		}

		// If update moves the group to a new parent, add it to new parent's children index
		if (newParentNodeId) {
			const newParentChildren = this.groupIdToChildrenIds.get(newParentNodeId) ?? new Set<string>();
			newParentChildren.add(groupNodeId);
			this.groupIdToChildrenIds.set(newParentNodeId, newParentChildren);
		}

		// Update the group's parentId
		this.groupIdToGroup.set(groupNodeId, { ...group, parentId: newParentNodeId });

		// Rebuild path cache for this group and all descendants
		this.buildPathCache(groupNodeId);
	}

	public renameGroup(groupNodeId: string, newName: string): void {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		this.groupIdToGroup.set(groupNodeId, { ...group, name: newName });
	}

	public setDeviceGroup(deviceNodeId: string, groupNodeId: string): void {
		const oldGroupId = this.deviceIdToGroupId.get(deviceNodeId);

		if (oldGroupId) {
			this.groupIdToMemberDeviceIds.get(oldGroupId)?.delete(deviceNodeId);
		}

		this.deviceIdToGroupId.set(deviceNodeId, groupNodeId);

		const memberDeviceIds = this.groupIdToMemberDeviceIds.get(groupNodeId) ?? new Set<string>();
		memberDeviceIds.add(deviceNodeId);
		this.groupIdToMemberDeviceIds.set(groupNodeId, memberDeviceIds);
	}

	public addOrUpdateCloudRoom(cloudRoom: CloudRoom): void {
		this.cloudRoomIdToCloudRoom.set(cloudRoom.id, cloudRoom);
	}

	public removeCloudRoom(cloudRoomNodeId: string): void {
		const cloudRoom = this.cloudRoomIdToCloudRoom.get(cloudRoomNodeId);

		if (!cloudRoom) {
			throw storeUsageError(
				`cloudRoom "${cloudRoomNodeId}" was not found in the store. Ensure the passed cloudRoom nodeId is correct.`
			);
		}

		// Remove device-to-room mappings, but keep devices in their group
		for (const deviceId of this.roomIdToMemberDeviceIds.get(cloudRoomNodeId) ?? []) {
			this.deviceIdToRoomId.delete(deviceId);
		}

		this.roomIdToMemberDeviceIds.delete(cloudRoomNodeId);
		this.cloudRoomIdToCloudRoom.delete(cloudRoomNodeId);
	}

	public setDeviceCloudRoom(deviceNodeId: string, cloudRoomNodeId: string): void {
		const oldCloudRoomId = this.deviceIdToRoomId.get(deviceNodeId);

		if (oldCloudRoomId) {
			this.roomIdToMemberDeviceIds.get(oldCloudRoomId)?.delete(deviceNodeId);
		}

		this.deviceIdToRoomId.set(deviceNodeId, cloudRoomNodeId);

		const memberDeviceIds = this.roomIdToMemberDeviceIds.get(cloudRoomNodeId) ?? new Set<string>();
		memberDeviceIds.add(deviceNodeId);
		this.roomIdToMemberDeviceIds.set(cloudRoomNodeId, memberDeviceIds);
	}

	public removeDevice(deviceNodeId: string): void {
		const groupId = this.deviceIdToGroupId.get(deviceNodeId);

		if (groupId) {
			this.groupIdToMemberDeviceIds.get(groupId)?.delete(deviceNodeId);
			this.deviceIdToGroupId.delete(deviceNodeId);
		}

		const cloudRoomId = this.deviceIdToRoomId.get(deviceNodeId);

		if (cloudRoomId) {
			this.roomIdToMemberDeviceIds.get(cloudRoomId)?.delete(deviceNodeId);
			this.deviceIdToRoomId.delete(deviceNodeId);
		}
	}

	public clear(): void {
		this.groupIdToGroup.clear();
		this.cloudRoomIdToCloudRoom.clear();
		this.groupIdToChildrenIds.clear();
		this.groupIdToMemberDeviceIds.clear();
		this.roomIdToMemberDeviceIds.clear();
		this.deviceIdToGroupId.clear();
		this.deviceIdToRoomId.clear();
		this.groupIdToIdPath.clear();
	}

	//
	// Helpers
	//
	private buildPathCache(groupNodeId: string): void {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		const parentPath = group.parentId !== undefined ? (this.groupIdToIdPath.get(group.parentId) ?? []) : [];

		this.groupIdToIdPath.set(groupNodeId, [...parentPath, groupNodeId]);

		for (const childId of this.groupIdToChildrenIds.get(groupNodeId) ?? []) {
			this.buildPathCache(childId);
		}
	}
}
